import { ApisauceInstance, create, Monitor, RequestTransform } from 'apisauce';
import firebase from 'firebase/compat/app';
import 'firebase/auth';
import envBase from 'env/.public-env.json';

import captureException, { addBreadCrumb } from './captureException';
import { AUTH_ERROR_MSG_KEY } from '../screens/Login/ErrorBanner';
import { clearCachedSelectedAccountId } from '../contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountPickerProvider';
import getLocalStorageImpersonatorId from '../contextProviders/SplashScreenProviders/UserAndAccountProviders/getLocalStorageImpersonatorId';

export const VIEW_AS_USER_ID_HEADER = 'x-view-as-user-id';

// STRIPE_LOCAL_URL and USE_LOCAL_INSIGHTS are only present in the gitignored
// local env/.public-env.json
const env = envBase as typeof envBase & {
  STRIPE_LOCAL_URL?: string;
  USE_LOCAL_INSIGHTS?: boolean;
};

const buildCloudFunctionClient = ({
  accountId,
  buildNumber,
  timezone,
}: {
  accountId?: string;
  buildNumber?: number;
  timezone?: string;
}) => {
  const api = create({
    baseURL: env.USE_LOCAL_FUNCTIONS
      ? `http://localhost:5000/${env.PROJECT_ID}/us-east4`
      : env.API_GATEWAY_URL,
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      'x-account-id': accountId,
      'x-build-number': buildNumber,
      'x-time-zone': timezone,
    },
  });
  addProblemMonitor(api);
  setAuthorizationToken(api);
  setViewAsHeader({ api, accountId });

  // When STRIPE_LOCAL_URL is set, route stripe requests through the Vite dev
  // server (localhost:3000) so its server.proxy can forward them to the local
  // Firebase emulator without browser CORS restrictions. All other services
  // (user_permissions, graphql, etc.) continue to use the staging gateway.
  if (env.STRIPE_LOCAL_URL) {
    api.axiosInstance.interceptors.request.use((config) => {
      if (config.url?.includes('/v1/stripe/')) {
        config.baseURL = 'http://localhost:3000';
      }
      return config;
    });
  }

  // When USE_LOCAL_INSIGHTS is set, route the insights query through the Vite
  // dev server (localhost:3000) so its server.proxy can forward it to the local
  // Firebase Functions emulator without browser CORS restrictions. All other
  // services continue to use the API gateway.
  if (env.USE_LOCAL_INSIGHTS) {
    // Each path here needs a matching server.proxy route in vite.config.ts.
    const LOCAL_INSIGHTS_PATHS = [
      '/v1/content/insightsQuery',
      '/v1/content/insightsAgentKb',
    ];
    api.axiosInstance.interceptors.request.use((config) => {
      if (LOCAL_INSIGHTS_PATHS.some((path) => config.url?.includes(path))) {
        config.baseURL = 'http://localhost:3000';
      }
      return config;
    });
  }

  return api;
};

export const problemMonitor: Monitor = (response) => {
  if (
    (response.status === 401 &&
      response.data.error === 'Unauthenticated: Token revoked') ||
    (response.status === 403 &&
      response.data.error ===
        'Forbidden: Session identity provider not permitted')
  ) {
    const errorMessage =
      'Your organization has enabled SSO. Please sign in with Microsoft';
    window.localStorage.setItem(AUTH_ERROR_MSG_KEY, errorMessage);
    clearCachedSelectedAccountId();
    firebase.auth().signOut().then();

    return;
  }

  // The bonus rewards endpoint 400s/404s when a driver has no record for the
  // selected period (e.g. after switching to a period they aren't in from the
  // board slideout). That's an expected, handled case — the slideout deselects
  // the driver and toasts — so don't report it to Sentry as noise.
  const isExpectedMissingBonusReward =
    !!response.config?.url?.includes('/v1/driver_bonus/rewards') &&
    (response.status === 400 || response.status === 404);
  if (isExpectedMissingBonusReward) {
    return;
  }

  // Additional (recognition) payment writes 400 on breaking a cap or a closed
  // period. The modal shows the server's message inline, so it's not noise
  // worth reporting.
  const isExpectedAdditionalPaymentRejection =
    !!response.config?.url?.includes('/v1/driver_bonus/additional_payments') &&
    response.status === 400;
  if (isExpectedAdditionalPaymentRejection) {
    return;
  }

  if (response.problem) {
    if (response.data && response.data.errorMessageWithGuid) {
      addBreadCrumb({
        type: 'error',
        message: 'errorMessageWithGuid',
        data: response.data.errorMessageWithGuid,
      });
    }

    addBreadCrumb({
      type: 'error',
      message: 'response',
      data: response,
    });
    if (response.config) {
      const newError = new Error(
        `${response.problem} at ${response.config.url}`,
      );
      newError.name = `${response.config.method} ${response.config.url}: ${response.status}`;

      captureException(newError);
    }
  }
};

export const addProblemMonitor = (api: ApisauceInstance) => {
  api.addMonitor(problemMonitor);
};

// The headers of a request being transformed. axios types them as optional,
// though they're always set by the time a transform runs.
export const getRequestHeaders = (request: Parameters<RequestTransform>[0]) => {
  if (!request.headers) {
    request.headers = {};
  }
  return request.headers;
};

export const setAuthorizationToken = (api: ApisauceInstance) => {
  api.addAsyncRequestTransform((request) => async () => {
    const user = firebase.auth().currentUser;
    if (user) {
      const token = await user.getIdToken();
      getRequestHeaders(request).authorization = `Bearer ${token}`;
    }
  });
};

// When "View app as" is active, send the impersonated user's id so the backend
// authorizes the request against THAT user's permissions. Identity is unchanged
// — the real user's bearer token still authenticates the request, so writes,
// audit and logs stay attributed to the real user. Read fresh per request so
// starting/stopping impersonation (in any tab) takes effect without
// rebuilding the client. Only sent when the impersonation was started on this
// client's account.
//
// Applied ONLY to the main API gateway client — never to the analytics client
// (see AnalyticsProvider.createApi), which must not receive this header.
export const setViewAsHeader = ({
  api,
  accountId,
}: {
  api: ApisauceInstance;
  accountId: string | undefined;
}) => {
  api.addRequestTransform((request) => {
    const impersonatorId = getLocalStorageImpersonatorId({ accountId });
    if (impersonatorId) {
      getRequestHeaders(request)[VIEW_AS_USER_ID_HEADER] = impersonatorId;
    }
  });
};

export default buildCloudFunctionClient;
